import React, { useMemo, useState } from 'react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { HeaderPublic } from '../../../layout/header_public';
import { TrainingListControl, ComplianceItem } from "@/components/ui/TrainingListControl";
import { Training as TrainingModule } from '../../support/training/Training';
import { TOTPEnrollment } from './TOTPEnrollment';
import { ChangePassword } from './ChangePassword';
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthenticationState } from '@/store/useAuthenticationState';
import {
  getBlockingComplianceItems,
  getComplianceReturnPath,
  hasBlockingComplianceItems,
  withComplianceReturnPath
} from './complianceGate';

const GET_USER_COMPLIANCE_STATUS = gql`
  query GetUserComplianceStatus {
    getUserComplianceStatus {
      id
      name
      item
      type
      description
      pdf
      frequencyDays
      lastCompletionDate
      nextDueDate
      dueInDays
      status
      required
      source
      actionPath
      actionLabel
    }
  }
`;

const PROMOTE_COMPLIANCE_SESSION = gql`
  mutation PromoteComplianceSession {
    promoteComplianceSession {
      promoted
      pendingCompliance
      blockingCount
      token
    }
  }
`;

type ComplianceStatusProps = {
  onCleared: () => void;
  items?: ComplianceItem[] | null;
  heading?: string;
  promoteOnClear?: boolean;
};

const cloneComplianceItems = (items?: ComplianceItem[] | null): ComplianceItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({ ...item }));
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const getActionUrl = (actionPath?: string | null) => {
  const normalized = `${actionPath || ''}`.trim();
  if (!normalized) {
    return null;
  }

  try {
    return new URL(normalized, window.location.origin);
  } catch (_error) {
    return null;
  }
};

const getActionPathname = (actionPath?: string | null) => {
  const actionUrl = getActionUrl(actionPath);
  if (actionUrl?.pathname) {
    return actionUrl.pathname;
  }
  const [pathname] = `${actionPath || ''}`.split('?');
  return `${pathname || ''}`.trim();
};

const getActionToken = (actionPath?: string | null) => {
  const actionUrl = getActionUrl(actionPath);
  return `${actionUrl?.searchParams.get('token') || ''}`.trim() || null;
};

export const ComplianceStatus = ({
  onCleared,
  items: initialItems,
  heading = 'Compliance Required',
  promoteOnClear = true
}: ComplianceStatusProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authenticationState, setAuthenticationStateJWT } = useAuthenticationState();
  const hasInitialItems = Array.isArray(initialItems);
  const [complianceItems, setComplianceItems] = useState<ComplianceItem[]>(
    hasInitialItems ? cloneComplianceItems(initialItems) : []
  );
  const [hasLoadedCompliance, setHasLoadedCompliance] = useState(hasInitialItems);
  const [showComplianceGate, setShowComplianceGate] = React.useState(false);
  const [activeTrainingItem, setActiveTrainingItem] = useState<ComplianceItem | null>(null);
  const [activeTOTPItem, setActiveTOTPItem] = useState<ComplianceItem | null>(null);
  const [activePasswordItem, setActivePasswordItem] = useState<ComplianceItem | null>(null);
  const gateClearedRef = React.useRef(false);
  const [fetchCompliance, { loading, error }] = useLazyQuery(GET_USER_COMPLIANCE_STATUS, {
    fetchPolicy: 'network-only'
  });
  const [promoteComplianceSession] = useMutation(PROMOTE_COMPLIANCE_SESSION);

  const promoteSessionIfNeeded = React.useCallback(async () => {
    if (!authenticationState) {
      return;
    }
    try {
      const result = await promoteComplianceSession();
      const nextToken = result.data?.promoteComplianceSession?.token;
      if (nextToken) {
        setAuthenticationStateJWT(nextToken);
      }
    } catch (promotionError) {
      console.error('Unable to promote compliance session', promotionError);
    }
  }, [authenticationState, promoteComplianceSession, setAuthenticationStateJWT]);

  const refreshComplianceItems = React.useCallback(
    async (attempts = 3) => {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        const result = await fetchCompliance();
        const nextItems = result.data?.getUserComplianceStatus;
        if (Array.isArray(nextItems)) {
          return cloneComplianceItems(nextItems);
        }

        if (attempt < attempts) {
          await sleep(200);
        }
      }

      return null;
    },
    [fetchCompliance]
  );

  React.useEffect(() => {
    if (hasLoadedCompliance) return;
    const loadCompliance = async () => {
      try {
        const nextItems = await refreshComplianceItems(2);
        if (Array.isArray(nextItems)) {
          setComplianceItems(nextItems);
        }
      } finally {
        setHasLoadedCompliance(true);
      }
    };
    loadCompliance();
  }, [hasLoadedCompliance, refreshComplianceItems]);

  const items: ComplianceItem[] = useMemo(() => complianceItems, [complianceItems]);

  React.useEffect(() => {
    if (!hasLoadedCompliance) return;
    if (loading) return;
    const gate = hasBlockingComplianceItems(items);
    if (!gate) {
      if (gateClearedRef.current) {
        return;
      }
      gateClearedRef.current = true;
      void (async () => {
        if (promoteOnClear) await promoteSessionIfNeeded();
        onCleared();
      })();
      return;
    }
    gateClearedRef.current = false;
    setShowComplianceGate(true);
  }, [hasLoadedCompliance, loading, items, onCleared, promoteOnClear, promoteSessionIfNeeded]);

  const filteredRequiredItems = useMemo(() => {
    return getBlockingComplianceItems(items);
  }, [items]);

  const handleComplianceItemCompleted = React.useCallback(
    async (completedItem: string) => {
      const refreshedItems = await refreshComplianceItems(3);
      if (Array.isArray(refreshedItems)) {
        setComplianceItems(refreshedItems);
      } else {
        // Keep users moving if the first refresh races with token refresh.
        setComplianceItems((previousItems) =>
          previousItems.filter((entry) => entry.item !== completedItem)
        );
      }
      setHasLoadedCompliance(true);
    },
    [refreshComplianceItems]
  );

  const handleOpen = (item: ComplianceItem) => {
    if (item.actionPath) {
      const pathname = getActionPathname(item.actionPath);
      if (pathname === '/resetpassword') {
        setActiveTrainingItem(null);
        setActiveTOTPItem(null);
        setActivePasswordItem(item);
        return;
      }
      if (pathname === '/support/totp-enrollment') {
        setActiveTrainingItem(null);
        setActivePasswordItem(null);
        setActiveTOTPItem(item);
        return;
      }

      const returnPath = getComplianceReturnPath(location.pathname, location.search);
      navigate(withComplianceReturnPath(item.actionPath, returnPath));
      return;
    }

    setActiveTOTPItem(null);
    setActivePasswordItem(null);
    setActiveTrainingItem(item);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        <HeaderPublic />
        <div className="flex-1 w-full flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-3xl bg-white rounded shadow p-6 text-red-600">Unable to complete login. Please retry.</div>
        </div>
      </div>
    );
  }

  if (showComplianceGate) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col relative">
        <HeaderPublic />
        <div className="flex-1 w-full flex items-start justify-center px-0 py-2">
          <div className="w-full bg-white rounded shadow p-2">
            <TrainingListControl 
                requiredItems={filteredRequiredItems}
                optionalItems={[]} // Only display required items
                onOpen={handleOpen}
                header={
                    <div className="flex items-center justify-between mb-4 px-4 pt-2">
                        <div className="text-lg font-semibold text-gray-900">{heading}</div>
                    </div>
                }
            />
          </div>
        </div>

        {activeTrainingItem && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl h-[95vh] flex flex-col overflow-hidden">
                     <div className="p-4 border-b flex justify-between items-center bg-gray-50 shrink-0">
                        <h3 className="font-semibold text-lg">{activeTrainingItem.name || activeTrainingItem.item}</h3>
                        <Button variant="outline" onClick={() => setActiveTrainingItem(null)}>
                             Close / Back
                        </Button>
                     </div>
                     <div className="flex-1 overflow-auto bg-gray-100 p-0 md:p-4 flex flex-col items-center">
                        <div className="w-full h-full bg-white rounded shadow-sm overflow-hidden flex flex-col">
                            <TrainingModule
                                embedded
                                item={activeTrainingItem.item}
                                onDone={async () => {
                                    const completedItem = activeTrainingItem.item;
                                    await handleComplianceItemCompleted(completedItem);
                                    setActiveTrainingItem(null);
                                }}
                            />
                        </div>
                     </div>
                </div>
            </div>
        )}

        {activeTOTPItem && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[95vh] flex flex-col overflow-hidden">
                    <div className="p-4 border-b flex justify-between items-center bg-gray-50 shrink-0">
                        <h3 className="font-semibold text-lg">{activeTOTPItem.name || activeTOTPItem.item}</h3>
                        <Button variant="outline" onClick={() => setActiveTOTPItem(null)}>
                            Close / Back
                        </Button>
                    </div>
                    <div className="flex-1 overflow-auto bg-gray-100 p-0 md:p-4">
                        <TOTPEnrollment
                          embedded
                          initialEnrollmentToken={getActionToken(activeTOTPItem.actionPath)}
                          onComplete={async () => {
                            const completedItem = activeTOTPItem.item;
                            await handleComplianceItemCompleted(completedItem);
                            setActiveTOTPItem(null);
                          }}
                        />
                    </div>
                </div>
            </div>
        )}

        {activePasswordItem && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[95vh] flex flex-col overflow-hidden">
                    <div className="p-4 border-b flex justify-between items-center bg-gray-50 shrink-0">
                        <h3 className="font-semibold text-lg">{activePasswordItem.name || activePasswordItem.item}</h3>
                        <Button variant="outline" onClick={() => setActivePasswordItem(null)}>Close / Back</Button>
                    </div>
                    <div className="flex-1 overflow-auto bg-white">
                        <ChangePassword
                          embedded
                          onComplete={async () => {
                            const completedItem = activePasswordItem.item;
                            await handleComplianceItemCompleted(completedItem);
                            setActivePasswordItem(null);
                          }}
                        />
                    </div>
                </div>
            </div>
        )}
      </div>
    ); 
  }
  

  return (
    <></>
  );
};
